import { ScrollView, Alert } from "react-native";
import { PressableScale } from "pressto";
import { FC, memo, useEffect, useMemo } from "react";
import { useUnistyles } from "react-native-unistyles";
import { useUpdateAlarmOverride, useUpdateAlarmSilence } from "@src/services/api/hooks";
import { View, Text, Card, Banner } from "@src/components";
import { WeeklySchedule, type ScheduleHistoryEntry } from "@src/features/schedules/components/WeeklySchedule";
import { useRouter } from "expo-router";
import { ResponsePreview } from "@src/features/alarms/components/ResponsePreview";
import { RoutinePreview } from "@src/features/alarms/components/RoutinePreview";
import { ExceptionsPreview } from "@src/features/alarms/components/ExceptionsPreview";
import { TimezonePreview } from "@src/features/alarms/components/TimezonePreview";
import { AppliedDiffers } from "@src/features/alarms/components/AppliedDiffers";
import { OverridePreview } from "@src/features/alarms/components/OverridePreview";
import { SilencePreview } from "@src/features/alarms/components/SilencePreview";
import type { AlarmSchedule } from "@visioni/a3s-alarm-schedules";
import { useAlarmScheduleHistory } from "@visioni/a3s-alarm-schedules/app";
import { showErrorToast } from "@src/utils/toast";
import { useTenantId } from "@src/features/tenants";
import { NothingApplied } from "../components/NothingApplied";
import { areSchedulesEqual } from "@src/features/schedules/utils/schedule-utils";
import { useAmplitude } from "@src/hooks/useAmplitude";
import { ANALYTICS_EVENTS } from "@src/services/analytics/events";
import { getTimezoneFromAlarmDetail, isPristine } from "@src/features/alarms/utils/alarm-utils";
import { Icon } from "@src/components/Icon";
import { AlarmConfiguration } from "@visioni/a3s-alarm-schedules/schema";
import { SiteId, AssetCameraKey } from "@visioni/a3s-schema";
import { toISODateString } from "@visioni/a3s-codecs";
import { addWeeks, startOfWeek } from "date-fns";
import { toZonedTime, fromZonedTime } from "date-fns-tz";

interface AlarmSettingsContentProps {
  alarmData: AlarmConfiguration;
  siteId: string;
  cameraKey: string;
  showCameraSelection: boolean;
  safeAreaBottom: number;
  isLoading: boolean;
  isFetching: boolean;
  error: Error | null;
  readOnly?: boolean;
}

export const AlarmSettingsContent: FC<AlarmSettingsContentProps> = memo(
  ({
    alarmData,
    siteId,
    cameraKey,
    showCameraSelection,
    safeAreaBottom,
    isLoading,
    isFetching,
    error,
    readOnly = false,
  }) => {
    const router = useRouter();
    const { theme } = useUnistyles();
    const { track } = useAmplitude();
    const tenantId = useTenantId();
    const updateOverrideMutation = useUpdateAlarmOverride();
    const updateSilenceMutation = useUpdateAlarmSilence();
    const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    const pristine = isPristine(alarmData);
    const timezone = getTimezoneFromAlarmDetail(alarmData) || alarmData.settings?.timezone || "UTC";

    const configuredSchedule = alarmData.configured?.schedule;
    const firstScheduleApplying = alarmData.applied === null && !!configuredSchedule;

    const historyRange = useMemo(() => {
      const nowInTz = toZonedTime(new Date(), timezone);
      const fromInTz = startOfWeek(addWeeks(nowInTz, -4), { weekStartsOn: 1 });
      return {
        from: toISODateString(fromZonedTime(fromInTz, timezone)),
        to: toISODateString(new Date()),
      };
    }, [timezone]);

    const { data: historyData, isLoading: isHistoryLoading } = useAlarmScheduleHistory({
      siteId: SiteId.fromString(siteId),
      assetCameraKey: AssetCameraKey.fromString(cameraKey),
      from: historyRange.from,
      to: historyRange.to,
      enabled: !!siteId && !!cameraKey,
    });

    const scheduleHistory: ScheduleHistoryEntry[] | undefined = useMemo(() => {
      if (!historyData?.data?.entries) return undefined;
      return historyData.data.entries.map((entry) => ({
        schedule: entry.schedule as unknown as AlarmSchedule,
        activeFrom: entry.activeFrom,
        activeUntil: entry.activeUntil,
      }));
    }, [historyData]);

    let localSchedulesDiffer = false;
    if (alarmData.applied === "configured") {
      localSchedulesDiffer = false;
    } else if (alarmData.applied === null) {
      localSchedulesDiffer = configuredSchedule !== null && configuredSchedule !== undefined;
    } else if (typeof alarmData.applied === "object" && alarmData.applied !== null) {
      const appliedSchedule = alarmData.applied.schedule;
      localSchedulesDiffer = !areSchedulesEqual(configuredSchedule, appliedSchedule);
    }

    const cameraTimezone =
      alarmData.settings?.timezone ?? alarmData.configured?.schedule?.metadata?.timezone ?? "Not set";
    const showTimezoneMismatchBanner = cameraTimezone !== "Not set" && cameraTimezone !== userTimezone;

    useEffect(() => {
      console.log(`[AlarmSettings] Query state - camera: ${cameraKey}, site: ${siteId}`, {
        isLoading,
        isFetching,
        hasData: !!alarmData,
        error: error ? { message: error.message, name: error.name } : null,
        queryEnabled: !!siteId && !!cameraKey,
      });
    }, [cameraKey, siteId, isLoading, isFetching, alarmData, error]);

    return (
      <ScrollView
        style={{ flex: 1 }}
        contentContainerStyle={{
          paddingBottom: safeAreaBottom + (showCameraSelection ? theme.spacing["7xl"] : theme.spacing["9xl"]),
        }}
      >
        {/* Timezone Selection - Show when pristine (no timezone and no schedule) */}
        {pristine && (
          <View px="5xl" pt="4xl" bg="background" mt={showCameraSelection ? "5xl" : undefined}>
            <Text textStyle="xs/medium" color="textMuted" mb="lg">
              Timezone
            </Text>
            <TimezonePreview
              timezone={alarmData.settings?.timezone}
              onPress={
                readOnly ? undefined : (
                  () => router.push(`/tenants/${tenantId}/sites/${siteId}/cameras/${cameraKey}/alarms/timezone`)
                )
              }
              accessoryIcon={readOnly ? undefined : "chevronRight"}
            />
          </View>
        )}

        {/* Nothing Applied Card - Show when no applied schedule yet */}
        {firstScheduleApplying && (
          <View mt={showCameraSelection && alarmData.configured ? "none" : undefined}>
            <NothingApplied />
          </View>
        )}
        {/* Applied Schedule Card - Show when schedules differ */}
        {localSchedulesDiffer && alarmData.applied && typeof alarmData.applied === "object" && (
          <View mt={showCameraSelection ? "5xl" : undefined}>
            <AppliedDiffers
              applied={alarmData.applied}
              exceptions={alarmData.settings?.exceptions ?? []}
              cameraKey={cameraKey}
              siteId={siteId}
            />
          </View>
        )}

        {/* Schedule, Routine, and Exceptions Card */}
        <View px="5xl" bg="background" mt="7xl">
          <Card py="xl" px="2xl">
            {/* Weekly Schedule Component */}
            <WeeklySchedule
              key={`${cameraKey}-${alarmData.settings?.updatedAt ?? cameraKey}`}
              schedule={alarmData.configured?.schedule as AlarmSchedule | undefined}
              exceptions={alarmData.settings?.exceptions ?? []}
              timezone={timezone}
              scheduleHistory={scheduleHistory}
              isHistoryLoading={isHistoryLoading}
            />
          </Card>
          <View mb="lg" mt="6xl">
            <Text textStyle="xs/medium" color="textMuted">
              Current
            </Text>
          </View>

          {/* Override Preview - Show if override exists */}
          {alarmData.settings?.override && (
            <View>
              <OverridePreview
                override={alarmData.settings.override}
                timezone={timezone}
                onDelete={
                  readOnly ? undefined : (
                    () => {
                      Alert.alert("Remove Override", "Are you sure you want to remove this override?", [
                        {
                          text: "Cancel",
                          style: "cancel",
                        },
                        {
                          text: "Remove",
                          style: "destructive",
                          onPress: () => {
                            track(ANALYTICS_EVENTS.ALARM_OVERRIDE_SAVED, {
                              camera_id: cameraKey,
                              action: "removed",
                              tenant_id: tenantId,
                              site_id: siteId,
                            });
                            updateOverrideMutation.mutate(
                              {
                                siteId,
                                cameraKey,
                                override: null,
                                timezone,
                              },
                              {
                                onError: (err: unknown) => {
                                  showErrorToast({ message: "Error", description: "Failed to remove override" });
                                  console.error("Override mutation error:", err);
                                },
                              },
                            );
                          },
                        },
                      ]);
                    }
                  )
                }
              />
            </View>
          )}

          {/* Silence Preview - Show if silence exists */}
          {alarmData.settings?.silence && (
            <View style={{ marginTop: alarmData.settings?.override ? theme.spacing.xl : 0 }}>
              <SilencePreview
                silence={alarmData.settings.silence}
                timezone={timezone}
                onDelete={
                  readOnly ? undefined : (
                    () => {
                      Alert.alert("Remove Silence", "Are you sure you want to remove this silence period?", [
                        {
                          text: "Cancel",
                          style: "cancel",
                        },
                        {
                          text: "Remove",
                          style: "destructive",
                          onPress: () => {
                            track(ANALYTICS_EVENTS.ALARM_SILENCE_SAVED, {
                              camera_id: cameraKey,
                              action: "removed",
                              tenant_id: tenantId,
                              site_id: siteId,
                            });
                            updateSilenceMutation.mutate(
                              {
                                siteId,
                                cameraKey,
                                silence: null,
                                timezone,
                              },
                              {
                                onError: (err: unknown) => {
                                  showErrorToast({ message: "Error", description: "Failed to remove silence" });
                                  console.error("Silence mutation error:", err);
                                },
                              },
                            );
                          },
                        },
                      ]);
                    }
                  )
                }
              />
            </View>
          )}

          {/* Routine Section - always show to allow creating the first routine */}
          <View
            style={{ marginTop: alarmData.settings?.silence || alarmData.settings?.override ? theme.spacing.xl : 0 }}
          >
            {pristine && (
              <View mb="3xl">
                <Banner
                  variant="warning"
                  title="Timezone required"
                  message="You must configure a timezone before managing the routine."
                />
              </View>
            )}
            <RoutinePreview
              routine={alarmData.settings?.routine ?? null}
              onPress={
                readOnly ? undefined : (
                  () => {
                    track(ANALYTICS_EVENTS.ALARM_ROUTINE_EDITED, {
                      camera_id: cameraKey,
                      tenant_id: tenantId,
                      site_id: siteId,
                    });
                    router.push(`/tenants/${tenantId}/sites/${siteId}/cameras/${cameraKey}/alarms/routine`);
                  }
                )
              }
              accessoryIcon={readOnly ? undefined : "chevronRight"}
              disabled={pristine}
            />
          </View>

          {/* Exceptions Section - Only show when camera is not pristine */}
          {!pristine && (
            <View mt="7xl">
              <View flexDirection="row" justifyContent="space-between" alignItems="center" mb="lg">
                <Text textStyle="xs/medium" color="textMuted">
                  Exceptions
                </Text>
                {!readOnly && (
                  <PressableScale
                    onPress={() =>
                      router.push(`/tenants/${tenantId}/sites/${siteId}/cameras/${cameraKey}/alarms/exceptions/create`)
                    }
                  >
                    <View w={36} justifyContent="center" alignItems="center">
                      <Icon name="plus" color={theme.colors.icon} />
                    </View>
                  </PressableScale>
                )}
              </View>

              <ExceptionsPreview
                exceptions={alarmData.settings?.exceptions ?? []}
                timezone={timezone}
                onExceptionPress={
                  readOnly ? undefined : (
                    (exceptionId) => {
                      track(ANALYTICS_EVENTS.ALARM_EXCEPTION_EDITED, {
                        camera_id: cameraKey,
                        exception_id: exceptionId,
                        tenant_id: tenantId,
                        site_id: siteId,
                      });
                      router.push(
                        `/tenants/${tenantId}/sites/${siteId}/cameras/${cameraKey}/alarms/exceptions/${exceptionId}`,
                      );
                    }
                  )
                }
                accessoryIcon={readOnly ? undefined : "chevronRight"}
              />
            </View>
          )}
        </View>

        {/* Response Section - Always show, use defaults if no response */}
        <View px="5xl" mt="7xl">
          <Text textStyle="xs/medium" color="textMuted" mb="lg">
            Alarm
          </Text>
          <ResponsePreview
            response={alarmData.settings?.response || null}
            onPress={
              readOnly ? undefined : (
                () => {
                  track(ANALYTICS_EVENTS.ALARM_RESPONSE_EDITED, {
                    camera_id: cameraKey,
                    tenant_id: tenantId,
                    site_id: siteId,
                  });
                  router.push(`/tenants/${tenantId}/sites/${siteId}/cameras/${cameraKey}/alarms/response`);
                }
              )
            }
            accessoryIcon={readOnly ? undefined : "chevronRight"}
          />
        </View>

        {/* Timezone Display */}
        <View px="5xl" mt="7xl">
          <Text textStyle="xs/medium" mb="3xl">
            Timezone
          </Text>
          {showTimezoneMismatchBanner && (
            <View mb="3xl">
              <Banner
                variant="warning"
                title="Timezone difference"
                message="All dates and times in alarm settings are shown in the camera's local time."
              />
            </View>
          )}
          <Text color="textMuted" textStyle="sm/regular">
            Camera: {cameraTimezone}
          </Text>
          <Text color="textMuted" textStyle="sm/regular" mb="4xl">
            You: {userTimezone}
          </Text>
        </View>
      </ScrollView>
    );
  },
);
